import { useAuthStore, useLanguageStore } from "@nasam/kernel";
import { io, type Socket } from "socket.io-client";

import {
  TrackingRequestError,
  type TrackingConnectionState,
  type TrackingListState,
  type TrackingListWatch,
  type TrackingScope,
  type TrackingSnapshot,
  type TrackingStreamEvent,
  type VehicleTrackingStream,
} from "../domain";
import {
  parseError,
  parseListCounts,
  parseListRemoved,
  parseListState,
  parseRemoved,
  parseSnapshot,
  parseTelemetry,
  parseVehicles,
} from "./trackingWire";

const ACK_TIMEOUT_MS = 10_000;

// Socket.IO's own typing of the feed. Payloads arrive as `unknown` and are
// parsed in trackingWire.ts.
interface ServerToClientEvents {
  "tracking:snapshot": (payload: unknown) => void;
  "tracking:telemetry": (payload: unknown) => void;
  "tracking:vehicles": (payload: unknown) => void;
  "tracking:removed": (payload: unknown) => void;
  "tracking:error": (payload: unknown) => void;
  "tracking:list:telemetry": (payload: unknown) => void;
  "tracking:list:vehicles": (payload: unknown) => void;
  "tracking:list:removed": (payload: unknown) => void;
  "tracking:list:counts": (payload: unknown) => void;
}

interface ClientToServerEvents {
  "tracking:subscribe": (payload: object, ack: (reply: unknown) => void) => void;
  "tracking:snapshot": (payload: object, ack: (reply: unknown) => void) => void;
  "tracking:viewport": (payload: object, ack: (reply: unknown) => void) => void;
  "tracking:list:watch": (payload: object, ack: (reply: unknown) => void) => void;
  "tracking:list:unwatch": (ack: (reply: unknown) => void) => void;
  "tracking:unsubscribe": (ack: (reply: unknown) => void) => void;
}

type RequestEvent =
  | "tracking:subscribe"
  | "tracking:snapshot"
  | "tracking:viewport"
  | "tracking:list:watch";

/** The scope as the feed reads it: only the properties that are set. */
function scopePayload(scope: TrackingScope): object {
  return {
    ...(scope.accountId === undefined ? {} : { accountId: scope.accountId }),
    ...(scope.bounds === undefined ? {} : { bounds: scope.bounds }),
    ...(scope.assetIds === undefined ? {} : { assetIds: scope.assetIds }),
  };
}

/** The watch as the feed reads it: only the properties that are set. */
function watchPayload(watch: TrackingListWatch): object {
  return {
    assetIds: watch.assetIds,
    ...(watch.search === undefined ? {} : { search: watch.search }),
    ...(watch.status === undefined ? {} : { status: watch.status }),
  };
}

/**
 * The live feed over Socket.IO (protocol v1, /live-docs): `/tracking` on the
 * API origin, the raw access token in `auth.token`. Follows one scope at a
 * time and keeps it across reconnects and token refreshes; a new stream id
 * (the feed restarted) is answered with a fresh snapshot rather than mixed
 * with the old one.
 */
export function createSocketTrackingStream(options: {
  readonly url: string;
  readonly path?: string;
}): VehicleTrackingStream {
  const socketOrigin = new URL(options.url, window.location.origin).origin;
  const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
    `${socketOrigin}/tracking`,
    {
      path: options.path ?? "/socket.io",
      transports: ["websocket"],
      autoConnect: false,
      // Read on every (re)connection, so a refreshed token is used.
      auth: (callback) =>
        callback({ token: useAuthStore.getState().accessToken ?? undefined }),
      reconnection: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 5_000,
    },
  );

  const listeners = new Set<(event: TrackingStreamEvent) => void>();
  const connectionListeners = new Set<(state: TrackingConnectionState) => void>();
  let scope: TrackingScope | null = null;
  let streamId: string | null = null;
  // Bumped by every scope change, so a late answer for an older scope (or a
  // reconnect's re-subscribe racing a new one) is dropped.
  let scopeVersion = 0;
  let resnapshotting = false;
  // The Asset List's watch, kept to re-send after a reconnect or a new
  // stream; versioned like the scope. While one is unanswered, counters are
  // dropped: they may still be the previous search's, and the answer brings
  // the new ones.
  let listWatch: TrackingListWatch | null = null;
  let listVersion = 0;
  let listPending = false;

  const publish = (event: TrackingStreamEvent) => {
    for (const listener of listeners) listener(event);
  };
  const publishConnection = (state: TrackingConnectionState) => {
    for (const listener of connectionListeners) listener(state);
  };

  function waitForConnection(): Promise<void> {
    if (socket.connected) return Promise.resolve();
    if (!socket.active) startConnection();
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        cleanup();
        reject(new TrackingRequestError({ code: "TRACKING_TIMEOUT", message: "" }));
      }, ACK_TIMEOUT_MS);
      const onConnect = () => {
        cleanup();
        resolve();
      };
      const cleanup = () => {
        window.clearTimeout(timer);
        socket.off("connect", onConnect);
      };
      socket.on("connect", onConnect);
    });
  }

  async function request(event: RequestEvent, payload: object): Promise<unknown> {
    await waitForConnection();
    let reply: unknown;
    try {
      reply = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck(event, payload);
    } catch {
      throw new TrackingRequestError({ code: "TRACKING_TIMEOUT", message: "" });
    }
    const error = parseError(reply);
    if (error !== null) throw new TrackingRequestError(error);
    return reply;
  }

  /** Asks for a scope's snapshot and, unless a newer scope took over
   *  meanwhile, replaces everything held with it. */
  async function loadSnapshot(
    event: "tracking:subscribe" | "tracking:snapshot",
    forScope: TrackingScope,
    version: number,
  ): Promise<TrackingSnapshot> {
    const reply = await request(event, scopePayload(forScope));
    const parsed = parseSnapshot(reply);
    if (parsed === null) {
      throw new TrackingRequestError({ code: "TRACKING_PAYLOAD_INVALID", message: "" });
    }
    if (version === scopeVersion) {
      streamId = parsed.streamId;
      publish({ type: "snapshot", snapshot: parsed.snapshot });
      // A reconnect lost the watch; a new stream restarted its sequences.
      rewatchList();
    }
    return parsed.snapshot;
  }

  async function sendListWatch(watch: TrackingListWatch, version: number): Promise<TrackingListState> {
    const reply = await request("tracking:list:watch", watchPayload(watch));
    const parsed = parseListState(reply);
    if (parsed === null) {
      throw new TrackingRequestError({ code: "TRACKING_PAYLOAD_INVALID", message: "" });
    }
    if (version === listVersion) {
      listPending = false;
      publish({ type: "list-watched", state: parsed.state });
    }
    return parsed.state;
  }

  function rewatchList() {
    if (listWatch === null) return;
    listPending = true;
    sendListWatch(listWatch, listVersion).catch((error: unknown) => {
      if (error instanceof TrackingRequestError) publish({ type: "error", error });
    });
  }

  /** A new scope (or none) ends the watch. */
  function endListWatch() {
    listWatch = null;
    listPending = false;
    listVersion += 1;
  }

  /** The feed restarted (a new stream id): its sequences start over, so
   *  everything is replaced from a fresh snapshot, once. */
  function resnapshot() {
    if (scope === null || resnapshotting) return;
    resnapshotting = true;
    loadSnapshot("tracking:snapshot", scope, scopeVersion)
      .catch((error: unknown) => {
        if (error instanceof TrackingRequestError) publish({ type: "error", error });
      })
      .finally(() => {
        resnapshotting = false;
      });
  }

  /** True when the batch belongs to the stream the client holds; otherwise
   *  starts a resnapshot and the batch is dropped. */
  const isCurrentStream = (id: string) => {
    if (id === streamId) return true;
    resnapshot();
    return false;
  };

  socket.on("tracking:telemetry", (value) => {
    const batch = parseTelemetry(value);
    if (batch === null || !isCurrentStream(batch.streamId)) return;
    if (batch.events.length > 0) publish({ type: "telemetry", events: batch.events });
  });
  socket.on("tracking:vehicles", (value) => {
    const batch = parseVehicles(value);
    if (batch === null || !isCurrentStream(batch.streamId)) return;
    publish({ type: "vehicles", vehicles: batch.vehicles });
  });
  socket.on("tracking:removed", (value) => {
    const batch = parseRemoved(value);
    if (batch === null || batch.streamId !== streamId) return;
    publish({ type: "removed", vehicleIds: batch.vehicleIds });
  });
  socket.on("tracking:list:telemetry", (value) => {
    const batch = parseTelemetry(value);
    if (batch === null || !isCurrentStream(batch.streamId)) return;
    if (batch.events.length > 0) publish({ type: "list-telemetry", events: batch.events });
  });
  socket.on("tracking:list:vehicles", (value) => {
    const batch = parseVehicles(value);
    if (batch === null || !isCurrentStream(batch.streamId)) return;
    publish({ type: "list-vehicles", vehicles: batch.vehicles });
  });
  socket.on("tracking:list:removed", (value) => {
    const batch = parseListRemoved(value);
    if (batch === null || batch.streamId !== streamId) return;
    // The feed stops watching them.
    if (listWatch !== null) {
      const removed = new Set(batch.vehicleIds);
      listWatch = { ...listWatch, assetIds: listWatch.assetIds.filter((id) => !removed.has(id)) };
    }
    publish({ type: "list-removed", vehicleIds: batch.vehicleIds, reason: batch.reason });
  });
  socket.on("tracking:list:counts", (value) => {
    const batch = parseListCounts(value);
    if (batch === null || batch.streamId !== streamId || listPending || listWatch === null) return;
    publish({ type: "list-counts", counts: batch.counts });
  });
  socket.on("tracking:error", (value) => {
    const error = parseError(value);
    if (error !== null) publish({ type: "error", error });
  });

  socket.on("connect", () => {
    publishConnection("connected");
    // Every reconnect re-sends the scope; its snapshot is authoritative.
    if (scope !== null) {
      loadSnapshot("tracking:subscribe", scope, scopeVersion).catch((error: unknown) => {
        if (error instanceof TrackingRequestError) publish({ type: "error", error });
      });
    }
  });
  socket.on("connect_error", () => publishConnection("reconnecting"));
  socket.on("disconnect", (reason) => {
    // The server ends a refused or revoked session itself (a tracking:error
    // naming why arrives first); Socket.IO doesn't retry that one.
    publishConnection(
      reason === "io client disconnect" || reason === "io server disconnect"
        ? "disconnected"
        : "reconnecting",
    );
  });

  function startConnection() {
    // Error messages come back in the app's language.
    socket.io.opts.query = { lang: useLanguageStore.getState().language };
    publishConnection(socket.connected ? "connected" : "connecting");
    socket.connect();
  }

  // A refreshed token takes a new handshake: reconnect, keeping the scope.
  useAuthStore.subscribe((state, previous) => {
    if (state.accessToken === previous.accessToken || !socket.active) return;
    socket.disconnect();
    if (state.accessToken !== null) startConnection();
  });

  return {
    connect: startConnection,
    disconnect() {
      scope = null;
      streamId = null;
      scopeVersion += 1;
      endListWatch();
      if (socket.connected) socket.emit("tracking:unsubscribe", () => undefined);
      socket.disconnect();
    },
    setScope(next) {
      scope = next;
      scopeVersion += 1;
      endListWatch();
      return loadSnapshot("tracking:subscribe", next, scopeVersion);
    },
    async setViewport(viewport) {
      if (scope === null) {
        throw new TrackingRequestError({ code: "TRACKING_NOT_SUBSCRIBED", message: "" });
      }
      const accountId = scope.accountId;
      scope = { ...viewport, ...(accountId === undefined ? {} : { accountId }) };
      const version = scopeVersion;
      const reply = await request("tracking:viewport", scopePayload(viewport));
      const parsed = parseSnapshot(reply);
      if (parsed === null) {
        throw new TrackingRequestError({ code: "TRACKING_PAYLOAD_INVALID", message: "" });
      }
      // What the moved scope holds: the rest is no longer followed.
      if (version === scopeVersion) publish({ type: "visible", vehicles: parsed.snapshot.vehicles });
      return parsed.snapshot;
    },
    async watchList(watch) {
      if (scope === null) {
        throw new TrackingRequestError({ code: "TRACKING_NOT_SUBSCRIBED", message: "" });
      }
      listWatch = watch;
      listVersion += 1;
      listPending = true;
      return sendListWatch(watch, listVersion);
    },
    unwatchList() {
      endListWatch();
      if (socket.connected) socket.emit("tracking:list:unwatch", () => undefined);
    },
    clearScope() {
      scope = null;
      streamId = null;
      scopeVersion += 1;
      endListWatch();
      if (socket.connected) socket.emit("tracking:unsubscribe", () => undefined);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    subscribeToConnection(listener) {
      connectionListeners.add(listener);
      return () => connectionListeners.delete(listener);
    },
  };
}
